import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import SearchableSelect from '../components/SearchableSelect';

const emptyRates = { contractType: 'monthly_contract', contractDays: '', fixedRate: '', fixedKm: '', extraKmRate: '' };
const id = value => String(value?._id || value || '');

export default function VehicleContractAgreement({ open, initialVehicleId = '', embedded = false, vehicles = [], contracts = [], onClose, onSaved }) {
  const [vehicleId, setVehicleId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientLocationId, setClientLocationId] = useState('');
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rates, setRates] = useState(emptyRates);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setVehicleId(''); setClientId(''); setClientLocationId(''); setLocations([]); setRates(emptyRates); setError(''); setMessage(''); return; }
    api.get('/clients?active=true').then(response => setClients(response.data.data || [])).catch(err => setError(err.response?.data?.message || 'Unable to load clients.'));
  }, [open]);

  useEffect(() => {
    if (!open || !initialVehicleId) return;
    const vehicle = vehicles.find(item => id(item) === initialVehicleId);
    const existing = contracts.find(contract => id(contract.vehicle) === initialVehicleId);
    if (!vehicle || !existing) return;
    setVehicleId(initialVehicleId);
    setClientId(id(vehicle.client));
    setClientLocationId(id(vehicle.clientLocation));
    setRates({ contractType: existing.contractType || 'monthly_contract', contractDays: existing.contractDays ?? '', fixedRate: existing.fixedRate, fixedKm: existing.fixedKm, extraKmRate: existing.extraKmRate });
    setError('');
    setMessage('');
  }, [open, initialVehicleId, vehicles, contracts]);

  useEffect(() => {
    if (!open || !clientId) { setLocations([]); return; }
    api.get(`/locations/client/${clientId}?active=true`).then(response => setLocations(response.data.data || [])).catch(err => setError(err.response?.data?.message || 'Unable to load client locations.'));
  }, [open, clientId]);

  const clientOptions = useMemo(() => clients.map(client => ({ value: id(client), label: client.name })).sort((left, right) => left.label.localeCompare(right.label)), [clients]);
  const locationOptions = useMemo(() => locations.map(location => ({ value: id(location), label: [location.name, location.city, location.state].filter(Boolean).join(' · ') })).sort((left, right) => left.label.localeCompare(right.label)), [locations]);
  const availableVehicles = useMemo(() => vehicles.filter(vehicle => (!clientId || id(vehicle.client) === clientId) && (!clientLocationId || id(vehicle.clientLocation) === clientLocationId)), [vehicles, clientId, clientLocationId]);
  const vehicleOptions = useMemo(() => availableVehicles.map(vehicle => {
    const saved = contracts.some(contract => id(contract.vehicle) === id(vehicle));
    const details = [vehicle.clientName, vehicle.clientLocationName].filter(Boolean).join(' · ');
    return { value: id(vehicle), label: `${vehicle.vehicleNumber}${details ? ` — ${details}` : ''}${saved ? ' (agreement saved)' : ''}` };
  }), [availableVehicles, contracts]);

  if (!open) return null;

  const selectVehicle = selectedId => {
    const existing = contracts.find(contract => id(contract.vehicle) === selectedId);
    const vehicle = vehicles.find(item => id(item) === selectedId);
    setVehicleId(selectedId);
    setClientId(vehicle ? id(vehicle.client) : '');
    setClientLocationId(vehicle ? id(vehicle.clientLocation) : '');
    setRates(existing ? { contractType: existing.contractType || 'monthly_contract', contractDays: existing.contractDays ?? '', fixedRate: existing.fixedRate, fixedKm: existing.fixedKm, extraKmRate: existing.extraKmRate } : emptyRates);
    setError('');
    setMessage('');
  };

  const submit = async event => {
    event.preventDefault();
    if (!vehicleId) { setError('Select a vehicle number.'); return; }
    if (!['rental', 'monthly_contract'].includes(rates.contractType)) { setError('Choose Rental or Monthly Contract.'); return; }
    const fixedRate = Number(rates.fixedRate), fixedKm = Number(rates.fixedKm), extraKmRate = Number(rates.extraKmRate);
    if (!(fixedRate > 0) || !(fixedKm > 0) || !Number.isFinite(extraKmRate) || extraKmRate < 0) {
      setError('Enter a fixed rate and fixed KM greater than zero, and an extra KM rate of zero or more.');
      return;
    }
    const contractDays = Number(rates.contractDays);
    if (!Number.isSafeInteger(contractDays) || contractDays <= 0) { setError('Enter contract days as a whole number greater than zero.'); return; }
    setSaving(true); setError(''); setMessage('');
    try {
      const response = await api.put(`/management/vehicle-contracts/${vehicleId}`, { contractType: rates.contractType, fixedRate, fixedKm, extraKmRate, contractDays });
      if (response.data.contractType !== rates.contractType) throw new Error('The server did not save the Contract Type. Restart the backend and try again.');
      onSaved(response.data);
      setRates({ contractType: response.data.contractType || 'monthly_contract', contractDays: response.data.contractDays ?? '', fixedRate: response.data.fixedRate, fixedKm: response.data.fixedKm, extraKmRate: response.data.extraKmRate });
      setMessage('Client agreement saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save the client agreement.');
    } finally { setSaving(false); }
  };

  const content = <section className={`ba-contract-modal ${embedded ? 'ba-contract-embedded' : ''}`} role={embedded ? undefined : 'dialog'} aria-modal={embedded ? undefined : 'true'} aria-labelledby="vehicle-contract-title">
      <header>
        <div><h3 id="vehicle-contract-title">Client Agreement</h3><p>Choose a client, delivery location, or vehicle number to set the agreed vehicle rates.</p></div>
        {!embedded && <button type="button" onClick={onClose} aria-label="Close client agreement">×</button>}
      </header>
      <form className="ba-contract-form" onSubmit={submit} noValidate>
        <label>Client
          <SearchableSelect value={clientId} onChange={value => { setClientId(value); setClientLocationId(''); setVehicleId(''); }} options={clientOptions} placeholder="Search or select client" />
        </label>
        <label>Client Location
          <SearchableSelect value={clientLocationId} onChange={value => { setClientLocationId(value); setVehicleId(''); }} options={locationOptions} placeholder={clientId ? 'Search or select client location' : 'Select a client first'} disabled={!clientId} />
        </label>
        <label className="wide">Vehicle Number
          <SearchableSelect value={vehicleId} onChange={selectVehicle} options={vehicleOptions} placeholder="Search or select vehicle number" required />
        </label>
        <label>Contract Type
          <select value={rates.contractType} onChange={event => setRates(current => ({ ...current, contractType: event.target.value }))} required>
            <option value="rental">Rental</option>
            <option value="monthly_contract">Monthly Contract</option>
          </select>
        </label>
        <label>Fixed Rate (₹)
          <input type="number" min="0.01" step="0.01" inputMode="decimal" value={rates.fixedRate} onChange={event => setRates(current => ({ ...current, fixedRate: event.target.value }))} required />
        </label>
        <label>Fixed KM
          <input type="number" min="0.01" step="0.01" inputMode="decimal" value={rates.fixedKm} onChange={event => setRates(current => ({ ...current, fixedKm: event.target.value }))} required />
        </label>
        <label>Contract Days
          <input type="number" min="1" step="1" inputMode="numeric" value={rates.contractDays} onChange={event => setRates(current => ({ ...current, contractDays: event.target.value }))} required />
        </label>
        <label>Extra KM Rate (₹/KM)
          <input type="number" min="0" step="0.01" inputMode="decimal" value={rates.extraKmRate} onChange={event => setRates(current => ({ ...current, extraKmRate: event.target.value }))} required />
        </label>
        {vehicleId && rates.fixedRate !== '' && rates.fixedKm !== '' && rates.extraKmRate !== '' && <p className="ba-contract-summary">₹{Number(rates.fixedRate || 0).toLocaleString('en-IN')} covers {Number(rates.fixedKm || 0).toLocaleString('en-IN')} KM. Additional distance is ₹{Number(rates.extraKmRate || 0).toLocaleString('en-IN')} per KM.</p>}
        {error && <p className="management-error wide" role="alert">{error}</p>}
        {message && <p className="ba-contract-success wide" role="status">{message}</p>}
        <div className="ba-contract-actions wide">{!embedded && <button type="button" onClick={onClose}>Close</button>}<button className="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save agreement'}</button></div>
      </form>
    </section>;
  return embedded ? content : <div className="ba-contract-backdrop" role="presentation">{content}</div>;
}
